import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

const getImageVariantInfo = listingImageConfig => {
  const {
    aspectWidth = 4,
    aspectHeight = 3,
    variantPrefix = 'listing-card',
    fit = 'crop',
  } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [
    `variants.${variantPrefix}`,
    `variants.${variantPrefix}-2x`,
    `variants.${variantPrefix}-4x`,
  ];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio, fit),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio, fit),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio, fit),
    },
  };
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_LISTINGS_RENTAL_PLACES_REQUEST =
  'app/ListingPage/SHOW_LISTINGS_RENTAL_PLACES_REQUEST';
export const FETCH_LISTINGS_RENTAL_PLACES_SUCCESS =
  'app/ListingPage/SHOW_LISTINGS_RENTAL_PLACES_SUCCESS';
export const FETCH_LISTINGS_RENTAL_PLACES_ERROR =
  'app/ListingPage/SHOW_LISTINGS_RENTAL_PLACES_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const SHOW_OWN_BRAND_PROFILE_LISTINGS_REQUEST =
  'app/ListingPage/SHOW_OWN_BRAND_PROFILE_LISTINGS_REQUEST';
export const SHOW_OWN_BRAND_PROFILE_LISTINGS_SUCCESS =
  'app/ListingPage/SHOW_OWN_BRAND_PROFILE_LISTINGS_SUCCESS';
export const SHOW_OWN_BRAND_PROFILE_LISTINGS_ERROR =
  'app/ListingPage/SHOW_OWN_BRAND_PROFILE_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  showListingsRentalPlacesError: null,
  fetchListingsRentalPlacesInProgress: false,
  listingsRentalPlaces: null,
  showOwnBrandProfileListingsError: null,
  showOwnBrandProfileListingsInProgress: false,
  ownBrandProfileListings: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_LISTINGS_RENTAL_PLACES_REQUEST:
      return {
        ...state,
        fetchListingsRentalPlacesInProgress: true,
        showListingsRentalPlacesError: null,
      };
    case FETCH_LISTINGS_RENTAL_PLACES_SUCCESS:
      return {
        ...state,
        fetchListingsRentalPlacesInProgress: false,
        listingsRentalPlaces: payload,
      };
    case FETCH_LISTINGS_RENTAL_PLACES_ERROR:
      return {
        ...state,
        showListingsRentalPlacesError: payload,
        fetchListingsRentalPlacesInProgress: false,
      };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    case SHOW_OWN_BRAND_PROFILE_LISTINGS_REQUEST:
      return {
        ...state,
        showOwnBrandProfileListingsInProgress: true,
        showOwnBrandProfileListingsError: null,
      };
    case SHOW_OWN_BRAND_PROFILE_LISTINGS_SUCCESS: {
      return {
        ...state,
        showOwnBrandProfileListingsInProgress: false,
        ownBrandProfileListings: payload,
      };
    }
    case SHOW_OWN_BRAND_PROFILE_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      return {
        ...state,
        showOwnBrandProfileListingsError: payload,
        showOwnBrandProfileListingsInProgress: false,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchListingsRentalPlacesRequest = () => ({
  type: FETCH_LISTINGS_RENTAL_PLACES_REQUEST,
});
export const fetchListingsRentalPlacesSuccess = listings => ({
  type: FETCH_LISTINGS_RENTAL_PLACES_SUCCESS,
  payload: listings,
});
export const fetchListingsRentalPlacesError = error => ({
  type: FETCH_LISTINGS_RENTAL_PLACES_ERROR,
  error: true,
  payload: error,
});

export const showOwnBrandProfileListingsRequest = requestAction(
  SHOW_OWN_BRAND_PROFILE_LISTINGS_REQUEST
);
export const showOwnBrandProfileListingsSuccess = successAction(
  SHOW_OWN_BRAND_PROFILE_LISTINGS_SUCCESS
);
export const showOwnBrandProfileListingsError = errorAction(SHOW_OWN_BRAND_PROFILE_LISTINGS_ERROR);

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

// ================ Thunks ================ //

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  const imageVariantInfo = getImageVariantInfo(config.layout.shopImage);

  const queryParams = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  const show = isOwn ? sdk.ownListings.show(queryParams) : sdk.listings.show(queryParams);

  return show
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => dispatch(showListingError(storableError(e))));
};

export const fetchListingsRentalPlaces = (storeId, config) => (dispatch, getState, sdk) => {
  const imageVariantInfo = getImageVariantInfo(config.layout.displayImage);

  //This is necessary in order to get the image dimensions as they appear on the transaction page
  const imageVariantInfoForCheckout = getImageVariantInfo(config.layout.listingImage);

  const queryParams = {
    pub_storeId: storeId.uuid,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [...imageVariantInfo.fieldsImage, ...imageVariantInfoForCheckout.fieldsImage],
    ...imageVariantInfo.imageVariants,
    ...imageVariantInfoForCheckout.imageVariants,
  };

  dispatch(fetchListingsRentalPlacesRequest());

  return sdk.listings
    .query({ ...queryParams })
    .then(response => {
      const listings = response.data?.data?.filter(
        l => l.attributes.state === 'published' && !l.attributes.publicData.deleted
      );

      const listingsWithImages = listings.map(l => {
        const listingImages = l.relationships.images.data;
        const includedImages = response.data.included.filter(i =>
          listingImages.some(li => li.id.uuid === i.id.uuid)
        );

        if (includedImages && includedImages.length > 0) {
          l.images = includedImages;
        }

        l.author = l.relationships.author.data;
        return l;
      });

      dispatch(fetchListingsRentalPlacesSuccess(listingsWithImages));

      return listingsWithImages;
    })
    .catch(e => {
      dispatch(fetchListingsRentalPlacesError(storableError(e)));
    });
};

export function fetchOwnBrandProfileListings(config) {
  return (dispatch, getState, sdk) => {
    dispatch(showOwnBrandProfileListingsRequest());
    return sdk.ownListings
      .query()
      .then(response => {
        const activeBrandListings = response.data?.data?.filter(l => {
          const { state, publicData } = l.attributes;
          const isDeleted = publicData.deleted ?? false; // Defaults to false if undefined

          return (
            (state === 'published' || state === 'closed') &&
            publicData.listingType === 'brand-listing' &&
            !isDeleted
          );
        });

        dispatch(showOwnBrandProfileListingsSuccess(activeBrandListings));
        return activeBrandListings;
      })
      .catch(e => dispatch(showOwnBrandProfileListingsError(storableError(e))));
  };
}

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  return Promise.all([
    dispatch(showListing(listingId, config)),
    dispatch(fetchReviews(listingId)),
  ]).then(response => {
    const listing = response[0].data.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';

    if (isBookingProcessAlias(transactionProcessAlias)) {
      // Fetch timeSlots.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }
    return response;
  });
};

export const fetchMonthlyTimeSlotsForDisplayListing = listing => (dispatch, getState, sdk) => {
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';

  if (isBookingProcessAlias(transactionProcessAlias)) {
    // Fetch timeSlots.
    // This can happen parallel to loadData.
    // We are not interested to return them from loadData call.
    fetchMonthlyTimeSlots(dispatch, listing);
  }
};
